// OneDrive:CoverageThreshold(50)

import {
  IPerson,
  EntityType,
  IPersonProfile
} from '../IPerson';
import { getServerUrl } from '../../../interfaces/ISpPageContext';
import { IDisposable } from '@ms/odsp-utilities/lib/interfaces/IDisposable';
import { IPeoplePickerCapabilities } from '../../../providers/peoplePicker/IPeoplePickerCapabilities';
import { IPeoplePickerDataSource, IPeoplePickerDataSourceParams as IOriginalPeoplePickerDataSourceParams } from '../IPeoplePickerDataSource';
import { IPeoplePickerQueryParams } from '../../../providers/peoplePicker/IPeoplePickerQueryParams';
import { IPickerEntityInformation } from '../IPickerEntityInformation';
import { ISpPageContext } from '../../../interfaces/ISpPageContext';
import { PresenceType } from '../IPresenceType';
import DataSource from '../../base/DataSource';
import PrincipalType from '../../roleAssignments/PrincipalType';
import CancelablePromise from '@ms/odsp-utilities/lib/async/Promise';
import { IGraphPeoplePickerHelper } from '../../../utilities/peoplePicker/GraphPeoplePickerHelper';
import { ISubstratePeoplePickerHelper, SUBSTRATE_SEARCH_URL } from '../../../utilities/peoplePicker/SubstratePeoplePickerHelper';
import { ApiUrlHelper } from '../../../utilities/url/ApiUrlHelper';
import { ItemUrlHelper } from '../../../utilities/url/ItemUrlHelper';
import { getUserPhotoUrl } from '../../../utilities/url/UserPhotoUrlHelper';
import DataRequestor from '@ms/odsp-graph/lib/base/dataRequestor/DataRequestor';
import IDataRequestor from '@ms/odsp-graph/lib/base/dataRequestor/IDataRequestor';
import { isGraphError } from '@ms/odsp-graph/lib/models/error/Error';
import { Killswitch } from '@ms/odsp-utilities/lib/killswitch/Killswitch';
import EmailAddressParser from '@ms/odsp-utilities/lib/string/EmailAddressParser';
import Uri from '@ms/odsp-utilities/lib/uri/Uri';
declare let O365Shell_Shim: any;

export interface IPeoplePickerDataSourceParams2 {
  imageAbsoluteWebUrl?: string;
}

export interface IPeoplePickerDataSourceDependencies {
  apiUrlHelper?: ApiUrlHelper;
  dataRequestor?: IDataRequestor;
  pageContext: ISpPageContext;
  graphPeoplePickerHelper?: IGraphPeoplePickerHelper;
  substratePeoplePickerHelper?: ISubstratePeoplePickerHelper;
  getAuthToken?: (resource: string) => Promise<string | undefined>;
}

export class PeoplePickerDataSource extends DataSource implements IPeoplePickerDataSource {
  private _apiUrlHelper: ApiUrlHelper;
  private _capabilities: IPeoplePickerCapabilities;
  private _imageAbsoluteWebUrl: string;
  private _skypeIntegration: any;
  private _dataRequestor: IDataRequestor;
  private _graphPeoplePickerHelper: IGraphPeoplePickerHelper;
  private _substratePeoplePickerHelper: ISubstratePeoplePickerHelper;
  private _getAuthToken: (resource: string) => Promise<string | undefined>;

  constructor(originalParams: IOriginalPeoplePickerDataSourceParams)
  constructor(params: IPeoplePickerDataSourceParams2, dependencies: IPeoplePickerDataSourceDependencies)
  constructor(paramsOrOriginalParams: IOriginalPeoplePickerDataSourceParams | IPeoplePickerDataSourceParams2, dependencies?: IPeoplePickerDataSourceDependencies) {
    const pageContext = isOriginalParams(paramsOrOriginalParams) ? paramsOrOriginalParams : dependencies.pageContext;
    super({}, {
      pageContext: pageContext
    });

    const apiUrlHelper = isOriginalParams(paramsOrOriginalParams) ? paramsOrOriginalParams.apiUrlHelper : dependencies.apiUrlHelper;

    this._imageAbsoluteWebUrl = paramsOrOriginalParams.imageAbsoluteWebUrl || '';
    this._apiUrlHelper = apiUrlHelper ? apiUrlHelper : new ApiUrlHelper({}, {
      itemUrlHelper: new ItemUrlHelper({}, { pageContext: pageContext }),
      pageContext: pageContext
    });

    this._capabilities = {
      supportsMruCaching: true
    };

    if (dependencies) {
      this._dataRequestor = dependencies.dataRequestor;
      this._graphPeoplePickerHelper = dependencies.graphPeoplePickerHelper;
      this._substratePeoplePickerHelper = dependencies.substratePeoplePickerHelper;
      this._getAuthToken = dependencies.getAuthToken;
    }
  }

  public getCapabilities(): IPeoplePickerCapabilities {
    return this._capabilities;
  }

  public getPersonaDetails(accountID: string): CancelablePromise<IPersonProfile> {
    const getDataPromise = (authToken?: string): CancelablePromise<IPersonProfile> => {
      return this.dataRequestor.getData({
        url: this._apiUrlHelper.build().segment('sp.publishing.personmagazine.GetPersonMagazineUserProfileDataForUser').parameter('accountName', accountID).toString(),
        qosName: 'GetPersonaDetails',
        authToken: authToken,
        parseResponse: (responseText: string): IPersonProfile => {
          let transformPerson = (rawPerson: any): IPerson => ({
            name: rawPerson.DisplayName,
            email: rawPerson.Email,
            userId: rawPerson.AccountName,
            office: rawPerson.Office,
            image: rawPerson.PictureUrl,
            job: rawPerson.Title,
            department: rawPerson.DepartmentName,
            phone: rawPerson.Phone,
            sip: rawPerson.Lync
          });

          let response: any = JSON.parse(responseText);
          let rawProfile = response.d.GetPersonMagazineUserProfileDataForUser;
          let ret: IPersonProfile = {
            personDetails: transformPerson(rawProfile.Primary),
            managerChain: rawProfile.ManagerChain.results.map(transformPerson),
            directReports: rawProfile.DirectReports.results.map(transformPerson)
          };

          ret.personDetails.profilePage = getServerUrl(this._pageContext) + "/_layouts/15/me.aspx?p=" + encodeURIComponent(ret.personDetails.sip) + "&origin=ProfileODBCard";

          return ret;
        }
      });
    }

    return CancelablePromise.resolve(
      this._getAuthToken && this._getAuthToken(this._pageContext.webAbsoluteUrl)
    ).then(getDataPromise);
  }

  public getPickerEntityInformation(person: IPerson): CancelablePromise<IPickerEntityInformation> {
    let groupId = 0;
    if (person.principalType === PrincipalType.sharePointGroup) {
      groupId = Number(person.rawPersonData.EntityData.SPGroupID);
    }

    const additionalPostData = JSON.stringify({
      entityInformationRequest: {
        PrincipalType: person.principalType,
        EmailAddress: person.email,
        GroupId: groupId
      }
    });

    const getDataPromise = (authToken?: string): CancelablePromise<IPickerEntityInformation> => {
      return this.dataRequestor.getData({
        url: this._apiUrlHelper.build().segment('SP.UI.ApplicationPages.ClientPeoplePickerWebServiceInterface.GetPickerEntityInformation').toString(),
        additionalPostData: additionalPostData,
        qosName: 'getPickerEntityInformation',
        authToken: authToken,
        noRedirect: true,
        parseResponse: (responseText: string): IPickerEntityInformation => {
          let response: any = JSON.parse(responseText);
          let entityInformation: IPickerEntityInformation = {
            totalMemberCount: response.d.TotalMemberCount,
            person: person
          };
          return entityInformation;
        }
      });
    }

    return CancelablePromise.resolve(
      this._getAuthToken && this._getAuthToken(this._pageContext.webAbsoluteUrl)
    ).then(getDataPromise);
  }

  public resolve(query: string, context: IPeoplePickerQueryParams): CancelablePromise<IPerson> {
    const getDataPromise = (authToken?: string): CancelablePromise<IPerson> => {
      return this.dataRequestor.getData({
        url: this._apiUrlHelper.build().segment('SP.UI.ApplicationPages.ClientPeoplePickerWebServiceInterface.ClientPeoplePickerResolveUser').toString(),
        additionalPostData: this._constructPostBody(query, context),
        qosName: 'Resolve',
        authToken: authToken,
        parseResponse: (responseText: string): IPerson => {
          let response: any = JSON.parse(responseText);
          let responseData: any = JSON.parse(response.d.ClientPeoplePickerResolveUser);
          let personArray: Array<any> = [];
          personArray[0] = responseData;

          if (Killswitch.isActivated("067ca2b4-2244-4153-8dfa-d11e6b4487bb", "03/19/2018", "Only overwrite display text if it is null or empty")) {
            if (!responseData.IsResolved) {
              personArray[0].DisplayText = responseData.Key;
            }
          }
          else {
            if (!responseData.IsResolved) {
              if (!!responseData.EntityData.IsBlockedOnODB && responseData.EntityData.IsBlockedOnODB.toLowerCase() === "true") {
                //do nothing for ODB policy.
              }
              else {
                personArray[0].DisplayText = responseData.Key;
              }
            }
          }

          personArray = this._transformData(personArray, context.filterExternalUsers);
          let person: IPerson = personArray[0];
          if (context.filterExternalUsers && person && person.entityType === EntityType.externalUser) {
            // If filterExternalUsers is passed then mark any existing external user in directory as unresolved
            // and change its entity type to default.
            person.isResolved = false;
            person.entityType = EntityType.regularUser;
          } else if (context.blockViaPolicy && !context.filterExternalUsers && person && !person.isResolved && person.multipleMatches.length === 0) {
            person.isResolved = true;
            person.isExternal = true;
            person.entityType = EntityType.externalUser;
          }
          if (context.isGraphDistributionList && context.useGraph && person.isExternal) {
            person.entityType = EntityType.group;
            person.principalType = PrincipalType.distributionList;
          }
          return person;
        }
      });
    }

    return CancelablePromise.resolve(
      this._getAuthToken && this._getAuthToken(this._pageContext.webAbsoluteUrl)
    ).then(getDataPromise);
  }

  public canUseGraph(context: IPeoplePickerQueryParams): boolean {
    const canUseGraph: boolean = context && context.useGraph && this._graphPeoplePickerHelper && this._graphPeoplePickerHelper.canUseGraph();
    if (canUseGraph && !this._dataRequestor) {
      this._dataRequestor = new DataRequestor({}, {});
    }

    return canUseGraph;
  }

  public canUseSubstrate(context: IPeoplePickerQueryParams): boolean {
    const canUseSubstrate = context.useSubstrate && this._substratePeoplePickerHelper && this._substratePeoplePickerHelper.canUseSubstrate();
    if (canUseSubstrate && !this._dataRequestor) {
      this._dataRequestor = new DataRequestor({}, {});
    }
    return canUseSubstrate;
  }

  public search(query: string, context: IPeoplePickerQueryParams): CancelablePromise<Array<IPerson>> {
    if (this.canUseSubstrate(context)) {
      return this._substrateSearch(query, context);
    } else if (this.canUseGraph(context)) {
      return this._graphSearch(query, context);
    } else {
      return this.sharePointSearch(query, context);
    }
  }

  public sharePointSearch(query: string, context: IPeoplePickerQueryParams): CancelablePromise<Array<IPerson>> {
    // Make sure to hide the search directory button when we're already searching via SP
    if (context.onToggleGetMoreResults) {
      context.onToggleGetMoreResults(false);
    }
    const getDataPromise = (authToken?: string): CancelablePromise<Array<IPerson>> => {
      return this.dataRequestor.getData({
        url: this._apiUrlHelper.build().segment('SP.UI.ApplicationPages.ClientPeoplePickerWebServiceInterface.ClientPeoplePickerSearchUser').toString(),
        additionalPostData: this._constructPostBody(query, context),
        qosName: 'Search',
        authToken: authToken,
        parseResponse: (responseText: string): Array<IPerson> => {
          const {
            allowSharePointGroups = true,
            filterExternalUsers,
            forceResolve
          } = context;

          let response: any = JSON.parse(responseText);
          let peopleArray: Array<any> = JSON.parse(response.d.ClientPeoplePickerSearchUser);
          let entities = this._transformData(peopleArray, filterExternalUsers);
          if (filterExternalUsers) {
            entities = entities.filter((person: IPerson) => person.entityType !== EntityType.externalUser);
          }

          const noResults = !entities.length;
          entities = entities.filter((person: IPerson) => {
            if (person.principalType === PrincipalType.sharePointGroup) {
              return allowSharePointGroups;
            } else if (person.principalType === PrincipalType.securityGroup) {
              /**
               * If SharePoint groups are allowed, that means ALL security groups are allowed as well.
               * If SharePoint groups aren't allowed, that means only email-enabled security groups are allowed.
               */
              return allowSharePointGroups || this._isValidEmail(person.email, true /* isSecurityGroup */);
            } else {
              return this._isValidEmail(person.email, false /* isSecurityGroup */);
            }
          });

          if (Killswitch.isActivated('cc7520a8-6922-4e12-81c1-179764750212', '3/29/2018', 'Disable limiting no results behavior to e-mail addresses.')) {
            if (forceResolve && noResults) {
              entities.push({
                name: query,
                userId: query,
                email: query
              });
            }
          } else {
            /**
             * If the query returned no results and the query is an email, add
             * that email as a suggestion.
             */
            if (
              context.resolveEmailIfNoResults !== false &&
              entities.length === 0 &&
              this._isValidEmail(query, false /* isSecurityGroup */)
            ) {
              const rawData = {
                Key: query,
                IsResolved: false
              };
              entities.push({
                email: query,
                entityType: EntityType.externalUser,
                isResolved: true,
                job: query,
                name: query,
                userId: query,
                rawData: rawData,
                rawPersonData: rawData,
                isExternal: true
              });
            }
          }
          return entities;
        }
      });
    }

    return CancelablePromise.resolve(
      this._getAuthToken && this._getAuthToken(this._pageContext.webAbsoluteUrl)
    ).then(getDataPromise);
  }

  public subscribePresence(accountID: string, fnCallback?: Function): IDisposable {
    try {
      this._ensureSkypePresence();

      if (!O365Shell_Shim.SkypeIntegration.isAvailable()) {
        return null; //If skypeIntegration is not available for this user, return null so that we can handle not showing presence gracefully.
      }

      let fnPresenceCallback = (type: any) => {
        try {
          switch (type) {
            case 0: // Microsoft.O365.SuiteApi.SkypeIntegration.SkypePresenceState.Unknown:
              fnCallback(PresenceType.Unknown);
              break;

            case 1: // Microsoft.O365.SuiteApi.SkypeIntegration.SkypePresenceState.Online:
              fnCallback(PresenceType.Online);
              break;

            case 2: // Microsoft.O365.SuiteApi.SkypeIntegration.SkypePresenceState.Busy:
              fnCallback(PresenceType.Busy);
              break;

            case 3: // Microsoft.O365.SuiteApi.SkypeIntegration.SkypePresenceState.DoNotDisturb:
              fnCallback(PresenceType.DoNotDisturb);
              break;

            case 4: // Microsoft.O365.SuiteApi.SkypeIntegration.SkypePresenceState.BeRightBack:
              fnCallback(PresenceType.BeRightBack);
              break;

            case 5: // Microsoft.O365.SuiteApi.SkypeIntegration.SkypePresenceState.Away:
              fnCallback(PresenceType.Away);
              break;

            case 6: // Microsoft.O365.SuiteApi.SkypeIntegration.SkypePresenceState.Offline:
              fnCallback(PresenceType.Offline);
              break;

            case 8: // Microsoft.O365.SuiteApi.SkypeIntegration.SkypePresenceState.Hidden:
              fnCallback(PresenceType.Hidden);
              break;

            default:
              fnCallback(PresenceType.Unknown);
              break;
          }
        } catch (e) {
          // do nothing
        }
      };

      this._skypeIntegration.TrackPresence(accountID, fnPresenceCallback);

      let disposable: IDisposable = {
        dispose: () => {
          try {
            this._skypeIntegration.UntrackPresence(accountID, fnPresenceCallback);
          } catch (e) {
            // do nothing
          }
        }
      };
      return disposable;
    } catch (e) {
      // do nothing
    }
    return null;
  }

  protected sendSourceName() {
    return 'PeoplePickerDataSource';
  }

  private _constructPostBody(query: string, context: IPeoplePickerQueryParams): string {
    let strBody = {
      queryParams: {
        'QueryString': query,
        'MaximumEntitySuggestions': context.maximumEntitySuggestions,
        'AllowEmailAddresses': context.allowEmailAddresses,
        'AllowOnlyEmailAddresses': context.allowOnlyEmailAddresses,
        'PrincipalType': context.principalType,
        'PrincipalSource': context.principalSource,
        'SharePointGroupID': context.groupID,
        'QuerySettings': context.querySettings
      }
    };
    return JSON.stringify(strBody);
  }

  private _ensureSkypePresence() {
    try {
      if (!Boolean(this._skypeIntegration)) {
        let skypeIntegration: any = new O365Shell_Shim.SkypeIntegration();
        skypeIntegration.OnApiReady(() => {
          this._skypeIntegration = skypeIntegration;
        });
      }
    } catch (e) {
      //do nothing. Basically skype presence didn't work so degrade gracefully
    }
  }

  private _graphSearch(query: string, context: IPeoplePickerQueryParams) {
    return this._graphPeoplePickerHelper.getGraphToken().then((oAuthToken: string) => {
      /**
       * If we don't have a valid auth token, the auth call failed and we should fallback to SP search.
       */
      if (!oAuthToken) {
        return this.sharePointSearch(query, context);
      }

      /**
       * Currently, mail-enabled security groups and legacy DLs are returned the same way from Graph. The former is an allowed share target while
       * the latter is not. Our two options for dealing with this are to:
       *   1. Call resolve when a user selects a PublicDistributionList returned by Graph. If it's a legacy DL, show an error message to the user.
       *   2. Filter out all PublicDistributionList objects returned by Graph and have the user click through to search the directory for more result
       * Putting this experiment behind a killswitch, not a flight, as calling Graph via the people picker is already behind a flight.
       */
      let graphRequestUrl: Uri = new Uri(`https://graph.microsoft.com/v1.0/me/people`);
      if (query) {
        graphRequestUrl.setQueryFromObject({
          $search: `"${query}"`,
          $top: '30',
          $select: 'displayName,scoredEmailAddresses,personType,department,jobTitle,userPrincipalName',
          $filter: 'personType/subclass ne \'PublicDistributionList\''
        });
      }

      return this._dataRequestor.send({
        apiName: 'Graph.PeopleSearch',
        path: graphRequestUrl.toString(),
        headers: {
          Authorization: 'Bearer ' + oAuthToken,
          'X-PeopleQuery-QuerySources': 'Mailbox,Directory'
        },
        needsAuthorization: false
      }).then((data: {
        value: any
      }) => {
        // Enable the "Search Directory" button for Graph search
        if (context.onToggleGetMoreResults) {
          context.onToggleGetMoreResults(true);
        }

        const rawPeople: Array<any> = data.value;
        const people: Array<IPerson> = [];
        rawPeople.forEach((rawPerson: any) => {
          const email = rawPerson.scoredEmailAddresses[0] ? rawPerson.scoredEmailAddresses[0].address : '';
          const key = rawPerson.userPrincipalName && this._isValidEmail(rawPerson.userPrincipalName, false /*isSecurityGroup*/) ? rawPerson.userPrincipalName : email;
          const rawData = { Key: key, IsResolved: false };
          const isImplicit = rawPerson.personType.subclass === 'ImplicitContact';
          const isOrganizationUser = rawPerson.personType.subclass === 'OrganizationUser';
          const isUnifiedGroup = rawPerson.personType.class === 'Group' && rawPerson.personType.subclass === 'UnifiedGroup';
          const isPublicDistributionList = rawPerson.personType.subclass === 'PublicDistributionList';

          let person: IPerson = {
            department: rawPerson.department,
            email: email,
            image: isImplicit ? undefined : getUserPhotoUrl({ userLoginName: email }),
            job: rawPerson.jobTitle || email,
            name: rawPerson.displayName,
            providerName: !isOrganizationUser ? undefined : 'Provider', // Needs to have a value for internal users.
            userId: key,
            rawData: rawData, // this what is actually passed to shareLink.
            rawPersonData: rawData,
            isExternal: !isOrganizationUser
          };

          if (isOrganizationUser) {
            person.entityType = EntityType.regularUser;
            person.principalType = PrincipalType.user;
            person.isResolved = true;
          } else if (isUnifiedGroup) {
            person.entityType = EntityType.group;
            person.principalType = PrincipalType.securityGroup;
            person.isResolved = false; // We still want to call resolve on unified groups so that we can get member count for UI
            person.userId = rawPerson.id; // Unified groups return a guid in rawPerson.userPrincipalName that won't resolve.
          } else {
            person.principalType = isPublicDistributionList ? PrincipalType.distributionList : PrincipalType.none;
            person.entityType = rawPerson.personType.class === 'Group' ? EntityType.group : EntityType.externalUser;
            person.isResolved = false;
            person.providerName = undefined;
          }

          if (!!person.userId) {
            people.push(person);
          }
        });
        /**
         * If the query returned no results and the query is an email, add
         * that email as a suggestion.
         */
        if (
          context.resolveEmailIfNoResults !== false &&
          people.length === 0 &&
          this._isValidEmail(query, false /* isSecurityGroup */)
        ) {
          const rawData = {
            Key: query,
            IsResolved: false // Set as false to sharing APIs verify user.
          };
          people.push({
            email: query,
            entityType: EntityType.externalUser,
            isResolved: true,
            job: query,
            name: query,
            userId: query,
            rawData: rawData,
            rawPersonData: rawData,
            isExternal: true
          });
        }
        return people;
      }, (error: Error) => {
        /**
         * Graph returning a 404 in search means that the resource to search doesn't exist. 401 means
         * the user did not authenticate correctly. 403 means the user doesn't have permission to call Graph.
         * Disable calls for the user and fallback to SP search in these cases.
         */
        if (isGraphError(error)) {
          switch (error.status) {
            case 400:
            case 401:
            case 403:
            case 404:
              this._graphPeoplePickerHelper.disableGraphCallsForPicker();
              return this.sharePointSearch(query, context);
            default:
              return this.sharePointSearch(query, context); // Handle error, probably
          }
        } else if (error.name !== 'Canceled') {
          this._graphPeoplePickerHelper.disableGraphCallsForPicker();
        }

        if (error.name === 'Canceled') {
          return null;
        } else {
          return this.sharePointSearch(query, context); // Handle error, probably
        }
      });
    });
  }

  private _substrateSearch(query: string, context: IPeoplePickerQueryParams) {
    return this._substratePeoplePickerHelper.getSubstrateToken().then((oAuthToken: string) => {
      /**
       * If we don't have a valid auth token, the auth call failed and we should fallback to SP search.
       */
      if (!oAuthToken) {
        return this.sharePointSearch(query, context);
      }

      return this._dataRequestor.send({
        apiName: 'Substrate.PeopleSearch',
        path: SUBSTRATE_SEARCH_URL + `/api/v1/suggestions`,
        headers: {
          Authorization: 'Bearer ' + oAuthToken,
          'X-User-Context': 'litoken=' + oAuthToken
        },
        needsAuthorization: false,
        requestType: 'POST',
        postData: JSON.stringify({
          EntityRequests: [{
            EntityType: 'People',
            ProvenanceMergeMode: 'Fallback',
            Provenances: [
              'Mailbox',
              'Directory',
              'LinkedIn'
            ],
            Query: {
              QueryString: query
            },
            From: 0,
            Size: 25,
            Fields: [
              // "Id",
              "DisplayName",
              "EmailAddresses",
              "CompanyName",
              "JobTitle",
              "OfficeLocation",
              "Department",
              // "ImAddress",
              // "PhoneNumber",
              // "ADObjectId",
              "UserPrincipalName",
              // "PersonaId",
              // "FeatureData",
              // "PDLItemId",
              "PeopleType",
              "PeopleSubtype"
              // "Confidence"
            ]
          }],
          TimeZone: 'UTC',
          Cvid: context.conversationId,
          Scenario: { 'Name': 'documentshare' }
        })
      }).then((data: {
        Groups: any[]
      }) => {
        // Enable the "Search Directory" button for Graph search
        if (context.onToggleGetMoreResults) {
          context.onToggleGetMoreResults(true);
        }

        const suggestions: Array<any> = data.Groups[0] ? data.Groups[0].Suggestions : [];
        const people: Array<IPerson> = [];
        suggestions.forEach((suggestion: any) => {
          const email = suggestion.EmailAddresses[0] || suggestion.QueryText || '';
          const key = suggestion.UserPrincipalName && this._isValidEmail(suggestion.UserPrincipalName, false /*isSecurityGroup*/) ? suggestion.UserPrincipalName : email;
          const rawData = { Key: key, IsResolved: false };
          const isImplicit = suggestion.PeopleSubtype === 'ImplicitContact';
          const isOrganizationUser = suggestion.PeopleSubtype === 'OrganizationUser';
          const isLinkedInContact = suggestion.PeopleSubtype === 'LinkedIn';
          const isUnifiedGroup = suggestion.PeopleType === 'Group' && suggestion.PeopleSubtype === 'UnifiedGroup';
          const isPublicDistributionList = suggestion.PeopleSubtype === 'PublicDistributionList';

          let person: IPerson = {
            department: suggestion.Department,
            office: suggestion.OfficeLocation,
            email: email,
            image: isImplicit ? undefined : getUserPhotoUrl({ userLoginName: email }),
            job: suggestion.JobTitle || email,
            name: suggestion.DisplayName,
            providerName: 'Provider', // Needs to have a value for internal users.
            userId: key,
            rawData: rawData, // this what is actually passed to shareLink.
            rawPersonData: rawData,
            isExternal: !isOrganizationUser
          };

          if (isOrganizationUser) {
            person.entityType = EntityType.regularUser;
            person.principalType = PrincipalType.user;
            person.isResolved = true;
          } else if (isUnifiedGroup) {
            person.entityType = EntityType.group;
            person.principalType = PrincipalType.securityGroup;
            person.isResolved = false; // We still want to call resolve on unified groups so that we can get member count for UI
          } else if (isLinkedInContact) {
            person.entityType = EntityType.linkedinUser;
          } else {
            person.principalType = isPublicDistributionList ? PrincipalType.distributionList : PrincipalType.none;
            person.entityType = suggestion.PeopleType === 'Group' ? EntityType.group : EntityType.externalUser;
            person.isResolved = false;
            person.providerName = undefined;
          }

          if (!!person.userId) {
            people.push(person);
          }
        });
        /**
         * If the query returned no results and the query is an email, add
         * that email as a suggestion.
         */
        if (
          context.resolveEmailIfNoResults !== false &&
          people.length === 0 &&
          this._isValidEmail(query, false /* isSecurityGroup */)
        ) {
          const rawData = {
            Key: query,
            IsResolved: false // Set as false to sharing APIs verify user.
          };
          people.push({
            email: query,
            entityType: EntityType.externalUser,
            isResolved: true,
            job: query,
            name: query,
            userId: query,
            rawData: rawData,
            rawPersonData: rawData,
            isExternal: true
          });
        }
        return people;
      }, (error: Error) => {
        if (error.name !== 'Canceled') {
          this._substratePeoplePickerHelper.disableSubstrateCallsForPicker();
          this.sharePointSearch(query, context);
        }
        return null;
      });
    });
  }

  private _isValidEmail(email: string, isSecurityGroup: boolean): boolean {
    /**
     * Users without an email address assigned are valid in ShareLink scenarios.
     * Security groups without an email address are not valid in ShareLink scenarios.
     */
    if (!email) {
      return !isSecurityGroup;
    }

    // Known input that causes issues (and cannot exist in a valid email address).
    if (email.indexOf(' ') > -1 || email.indexOf(';') > -1) {
      return false;
    }

    // If result has an associated email, ensure that it is valid
    if (!Killswitch.isActivated('85cfc348-6fc6-4b40-9578-1e669e8aeab1', '07/06/2018', 't-jasm, more restrictive email validation.')) {
      return EmailAddressParser.isValidEmailSyntax(email, false);
    } else {
      const re = /\S+@\S+\.\S+/;
      return re.test(email);
    }
  }

  private _transformData(data: Array<any>, filterExternalUsers: boolean): Array<IPerson> {
    return data.map((rawPerson: any) => {
      let rawEntityData = rawPerson.EntityData;
      let person: IPerson = {
        name: rawPerson.DisplayText,
        email: rawEntityData.Email,
        userId: rawPerson.Key,
        job: rawEntityData.Title,
        department: rawEntityData.Department,
        phone: rawEntityData.MobilePhone,
        providerName: rawPerson.ProviderDisplayName,
        entityType: rawPerson.EntityType,
        isResolved: rawPerson.IsResolved,
        multipleMatches: rawPerson.MultipleMatches,
        rawPersonData: rawPerson,
        //set "rawData" to be the value exactly returned by the server. For list metadata, we need this exact value
        //when we write back to the list.
        rawData: rawPerson
      };

      if (person.multipleMatches.length > 0) {
        person.multipleMatches = this._transformData(person.multipleMatches, filterExternalUsers);
        if (filterExternalUsers) {
          person.multipleMatches = person.multipleMatches.filter((person: IPerson) => person.entityType !== EntityType.externalUser);
        }
      }

      /**
       * If this._imageAbsoluteWebURL is set, it means that url should prepend the normal image URL
       */
      const email = person.email;
      if (person.isResolved) {
        if (email) {
          person.image = this._imageAbsoluteWebUrl + getUserPhotoUrl({ userLoginName: email });
        } else {
          person.image = null;
        }
      }

      // calculate entity type
      let serverType = rawEntityData.PrincipalType || rawPerson.EntityType;
      switch (serverType) {
        case "UNVALIDATED_EMAIL_ADDRESS":
        case "GUEST_USER":
          person.entityType = EntityType.externalUser;
          person.principalType = PrincipalType.user;
          person.isExternal = true;
          break;
        case "SecGroup":
        case "SharePointGroup":
        case "FormsRole":
          person.entityType = EntityType.group;
          if (serverType === "SharePointGroup") {
            person.principalType = PrincipalType.sharePointGroup;
          } else {
            person.principalType = PrincipalType.securityGroup;
          }
          break;
        default:
          person.entityType = EntityType.regularUser;
          person.principalType = PrincipalType.user;
      }

      return person;
    });
  }
}

function isOriginalParams(params: IOriginalPeoplePickerDataSourceParams | IPeoplePickerDataSourceParams2): params is IOriginalPeoplePickerDataSourceParams {
  const originalParams = <IOriginalPeoplePickerDataSourceParams>params;
  return !!originalParams.webAbsoluteUrl;
}
